import React from "react";
import { useFocusable, FocusContext } from "@noriginmedia/norigin-spatial-navigation";
import { getFormattedTime } from "./utils/shared/time";
import { trackPendoEvent } from "./utils/shared/pendo";
import { EVENT_STATUS, CONTENT_TYPE, SIDE_PANEL_ACTIONS, SIDE_PANEL_BUTTON_TITLES } from "./constants/event";
import { getSidePanelAction } from "./utils/shared/eventStatus";
import Button from "./components/Button";
import backArrow from "./assets/backArrow.svg";
import removeEventIcon from "../assets/removeEvent.svg";
import addEventIcon from "../assets/addEvent.svg";
import playIcon from "../assets/playNowIcon.svg";
import "./EPGSidePanel.scss";

const ACTION_ICON_MAP = {
    [SIDE_PANEL_ACTIONS.REMOVE]: removeEventIcon,
    [SIDE_PANEL_ACTIONS.PLAY]: playIcon,
    [SIDE_PANEL_ACTIONS.ADD]: addEventIcon,
};

const getEventState = (isUpcoming, isNowPlaying) => {
    if (isUpcoming) return "upcoming";
    if (isNowPlaying) return "nowPlaying";
    return "live";
};

const BackButton = ({ onEnterPress, light = false }) => {
    const { ref, focused } = useFocusable({
        focusKey: "SIDEPANEL_BACK_BUTTON",
        onEnterPress,
    });

    return (
        <div ref={ref}>
            <button className={`backBtn ${light ? "backBtn--light" : ""} ${focused ? "focused" : ""}`} onClick={onEnterPress} tabIndex={-1} aria-label="Close">
                <img src={backArrow} alt="Back" />
            </button>
        </div>
    );
};

const EPGSidePanel = ({ isLoading, onClose, event, isNowPlaying, onPlay, showBackdrop = false }) => {
    const { ref, focusKey } = useFocusable({
        isFocusBoundary: true, // Do not get out of this container
    });

    const isChannel = event.contentType === CONTENT_TYPE.LIVE_CHANNEL;
    const isSubscribed = event.isDeviceSubscribed === true;
    const nowPlayingBadgeText = isNowPlaying ? EVENT_STATUS.NOW_PLAYING : EVENT_STATUS.LIVE;
    const isUpcoming = !isChannel && [EVENT_STATUS.SCHEDULED, EVENT_STATUS.COMING].includes(event.status);
    const eventState = getEventState(isUpcoming, isNowPlaying);
    const badgeClass = `liveBadge ${eventState}`;
    const badgeText = isUpcoming ? `UPCOMING | ${getFormattedTime(event.schedule_start, "MM/DD")}` : nowPlayingBadgeText;

    const action = getSidePanelAction(event, isNowPlaying, isSubscribed);
    const isDestructiveAction = action === SIDE_PANEL_ACTIONS.REMOVE;
    const ctaText = SIDE_PANEL_BUTTON_TITLES[action];
    const ctaIcon = ACTION_ICON_MAP[action];

    const matchedLogos = !isChannel && event.teams ? event.teams.map((team) => <img key={team.id} src={team.logo} alt={team.display_name} className="teamLogo" />) : [];

    const onEnterPress = (event) => {
        // Track pendo event
        trackPendoEvent(`${ctaText}`, {
            event: event.title
        })
        onPlay(event);
    }

    return (
        <FocusContext.Provider value={focusKey}>
            {showBackdrop && <div className="panelBackdrop" />}
            <div className="panel">
                <BackButton onEnterPress={onClose} light={showBackdrop} />
                <div ref={ref} className="panelContainer">
                    <div className="header">
                        <span className={badgeClass}>{badgeText}</span>
                    </div>
                    {!!matchedLogos.length && <div className="teamLogosWrapper">{matchedLogos}</div>}
                    <h1 className="title">{event.title}</h1>
                    {!isChannel && (
                        <p className="meta">
                            Pregame:&nbsp;
                            {getFormattedTime(event.schedule_start)}
                            {" | "}Event start:&nbsp;
                            {getFormattedTime((event.information && event.information.main_start_time) || event.schedule_start)}
                            {event.hasPostgame && <span className="meta"> {" | "} Includes Postgame</span>}
                        </p>
                    )}
                    {event.description && <p className="eventDescription">{event.description}</p>}
                    {!isChannel && <p className="provider">{event.label}</p>}
                    <Button bgColor={isDestructiveAction || isLoading ? "rgba(255, 255, 255, 0.32)" : "#fff"} className="button Cta" event={event} icon={<img src={ctaIcon} alt="" />} loading={isLoading} onPlay={onEnterPress} text={ctaText} textColor={isDestructiveAction || isLoading ? "#fff" : "#000"} />
                </div>
            </div>
        </FocusContext.Provider>
    );
};

export default EPGSidePanel;
